import { Component, type ErrorInfo, type ReactNode } from "react"

import { Button } from "@coval/ui"
import { RefreshCw } from "lucide-react"

import { logger } from "@/lib/logger"
import CovalLogo from "@/components/icons/CovalLogo"

const log = logger("error-boundary")

interface Props {
  children: ReactNode
  /** Identifies which boundary caught the error in telemetry. */
  level: "app" | "route"
  /**
   * When this value changes, a previously-caught error is cleared. The route
   * boundary passes the current path so navigating away recovers the UI without
   * a full reload.
   */
  resetKey?: string
}

interface State {
  hasError: boolean
}

/**
 * Catches render-time errors in the React tree below it and shows a fallback
 * instead of an unmounted (white-screen) app. Errors are reported through the
 * shared logger, which forwards to Datadog RUM. Does not catch errors in event
 * handlers, async code, or server-side rendering.
 */
export class AppErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false }

  static getDerivedStateFromError(): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    log.error(`Unhandled render error (${this.props.level} boundary)`, error, {
      boundary: this.props.level,
      componentStack: info.componentStack,
    })
  }

  componentDidUpdate(prevProps: Props): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return <ErrorFallback level={this.props.level} />
    }
    return this.props.children
  }
}

function ErrorFallback({ level }: { level: Props["level"] }) {
  // The app boundary is the document-level fallback, so <main> is the correct
  // landmark. The route boundary renders inside a layout that already owns the
  // page's <main>, so use a neutral container to avoid two <main> landmarks.
  const Root: "main" | "div" = level === "app" ? "main" : "div"
  return (
    <Root className="flex min-h-screen items-center bg-surface-default px-6 py-12 text-coval-text">
      <section className="mx-auto w-full max-w-xl border-l border-coval-border pl-6 sm:pl-8">
        <div className="mb-8 flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-coval-contrast">
            <span className="dark:hidden">
              <CovalLogo theme="dark" width={14} height={14} />
            </span>
            <span className="hidden dark:block">
              <CovalLogo theme="light" width={14} height={14} />
            </span>
          </span>
          <span className="text-sm font-semibold tracking-wide">COVAL</span>
        </div>

        <p className="mb-3 text-xs font-medium uppercase tracking-wide text-coval-text-muted">
          Unexpected error
        </p>
        <h1 className="text-2xl font-semibold tracking-normal text-coval-text sm:text-3xl">
          Something went wrong
        </h1>
        <p className="mt-3 max-w-md text-sm leading-6 text-coval-text-muted">
          The error has been reported. Reload the app to start from a clean
          state.
        </p>

        <Button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-7"
        >
          <RefreshCw aria-hidden="true" />
          Reload app
        </Button>
      </section>
    </Root>
  )
}
